/* ═══════════════════════════════════════════════════════════════════
   In-memory profile/memory cache — zero-cost local persistence.

   • Survives SPA navigations (module-level Map).
   • Cleared on hard page reload — matches user's "most aggressive
     savings until app reload" preference.
   • Keyed by URL so paginated requests are cached independently.

   Public API:
     getCached(key)        → cached value or undefined
     setCached(key, value) → store value
     cachedFetch(url, axiosInstance) → returns response.data with
       transparent cache-first behavior (in-memory only).
   ═══════════════════════════════════════════════════════════════════ */

import axios from 'axios';

const store = new Map();

export const getCached = (key) => store.get(key);

export const setCached = (key, value) => { store.set(key, value); return value; };

export const clearCache = () => store.clear();

/**
 * cachedFetch(url) — cache-first GET.  On cache hit returns the
 * previously stored response data immediately (zero network calls).
 * On miss, performs the GET, caches it, and returns the data.
 */
export const cachedFetch = async (url, config = {}) => {
  if (store.has(url)) return store.get(url);
  const { data } = await axios.get(url, config);
  store.set(url, data);
  return data;
};

/** Invalidate a specific cached key (e.g., after a mutation). */
export const invalidate = (key) => { store.delete(key); };

/** Invalidate every key whose URL contains `substring`. */
export const invalidateMatching = (substring) => {
  for (const k of store.keys()) {
    if (k.includes(substring)) store.delete(k);
  }
};
